// PWA Installation Helper
export class PWAInstaller {
  constructor() {
    this.deferredPrompt = null;
    this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    this.isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.matchMedia('(display-mode: fullscreen)').matches ||
                       window.navigator.standalone === true;
    
    this.init();
  }
  
  init() {
    // Listen for beforeinstallprompt event (Android/Desktop Chrome)
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      this.deferredPrompt = e;
      this.showInstallPrompt();
    });
    
    // Check if running as PWA
    if (this.isStandalone) {
      console.log('Running as PWA in fullscreen mode');
      this.setupFullscreenFixes();
    } else if (this.isIOS && !this.hasShownIOSPrompt() && !this.isPromptDismissed()) {
      // Show iOS install instructions
      setTimeout(() => this.showIOSInstallPrompt(), 2000);
    }
  }
  
  setupFullscreenFixes() {
    // iOS PWA optimizations
    if (this.isIOS) {
      // Prevent overscroll/bounce effect
      document.body.addEventListener('touchmove', (e) => {
        if (e.target === document.body) {
          e.preventDefault();
        }
      }, { passive: false });
      
      // Log that we're in PWA mode
      console.log('Running as iOS PWA with translucent status bar');
    }
  }
  
  hasShownIOSPrompt() {
    return localStorage.getItem('soulforge_iosPromptShown') === 'true';
  }
  
  isPromptDismissed() {
    return localStorage.getItem('soulforge_pwaPromptDismissed') === 'true';
  }
  
  showIOSInstallPrompt() {
    // Create backdrop
    const backdrop = document.createElement('div');
    backdrop.className = 'pwa-modal-backdrop';
    backdrop.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.8);
      z-index: 10000;
      display: flex;
      align-items: center;
      justify-content: center;
    `;
    
    // Create modal
    const modal = document.createElement('div');
    modal.style.cssText = `
      background: rgba(0, 0, 0, 0.95);
      border: 2px solid #0f0;
      border-radius: 5px;
      padding: 25px 30px;
      color: #0f0;
      font-family: 'Berkeley Mono', 'Courier New', monospace;
      font-size: 12px;
      text-align: center;
      box-shadow: 0 0 30px rgba(0, 255, 0, 0.6);
      max-width: 350px;
      animation: pwaModalBoot 0.3s ease-out;
    `;
    
    modal.innerHTML = `
      <div style="margin-bottom: 15px; font-weight: bold; text-transform: uppercase; font-size: 14px;">
        Install for Fullscreen
      </div>
      <div style="margin-bottom: 20px; opacity: 0.9; line-height: 1.4;">
        Tap the Share button <span style="font-size: 16px;">⎘</span><br>
        then "Add to Home Screen"<br>
        <span style="font-size: 11px; opacity: 0.7;">for the best experience</span>
      </div>
      <div style="display: flex; flex-direction: column; gap: 10px;">
        <button id="iosLaterBtn" style="
          background: transparent;
          border: 1px solid #0f0;
          color: #0f0;
          padding: 8px 25px;
          cursor: pointer;
          font-family: inherit;
          text-transform: uppercase;
          font-size: 11px;
          transition: all 0.2s;
        ">Later</button>
        <button id="iosDontAskBtn" style="
          background: transparent;
          border: 1px solid rgba(0, 255, 0, 0.5);
          color: rgba(0, 255, 0, 0.7);
          padding: 6px 25px;
          cursor: pointer;
          font-family: inherit;
          text-transform: uppercase;
          font-size: 10px;
          transition: all 0.2s;
        ">Don't Ask Again</button>
      </div>
    `;
    
    backdrop.appendChild(modal);
    document.body.appendChild(backdrop);
    
    // Event handlers
    const closeModal = () => {
      backdrop.style.opacity = '0';
      modal.style.transform = 'scale(0.9) translateY(10px)';
      setTimeout(() => backdrop.remove(), 200);
    };
    
    // Later button
    document.getElementById('iosLaterBtn').addEventListener('click', () => {
      localStorage.setItem('soulforge_iosPromptShown', 'true');
      closeModal();
    });
    
    // Don't ask again button
    document.getElementById('iosDontAskBtn').addEventListener('click', () => {
      localStorage.setItem('soulforge_iosPromptShown', 'true');
      localStorage.setItem('soulforge_pwaPromptDismissed', 'true');
      closeModal();
    });
    
    // Click backdrop to close
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) {
        localStorage.setItem('soulforge_iosPromptShown', 'true');
        closeModal();
      }
    });
  }
  
  /**
   * Dismiss any currently shown install prompt
   * @param {boolean} dontAskAgain - If true, sets flag to never show prompt again
   */
  dismissCurrentPrompt(dontAskAgain = false) {
    // Find and remove any PWA modal backdrop
    const backdrop = document.querySelector('.pwa-modal-backdrop');
    if (backdrop) {
      if (dontAskAgain) {
        localStorage.setItem('soulforge_pwaPromptDismissed', 'true');
      }
      // Trigger close animation
      backdrop.style.opacity = '0';
      const modal = backdrop.querySelector('#pwaModal') || backdrop.querySelector('div');
      if (modal) {
        modal.style.transform = 'scale(0.9) translateY(10px)';
      }
      setTimeout(() => backdrop.remove(), 200);
      console.log('PWA prompt dismissed' + (dontAskAgain ? ' permanently' : ''));
      return true;
    }
    return false;
  }
  
  showInstallPrompt() {
    if (!this.deferredPrompt) return;
    
    // Check if user has opted out
    if (localStorage.getItem('soulforge_pwaPromptDismissed') === 'true') {
      return;
    }
    
    // Create backdrop
    const backdrop = document.createElement('div');
    backdrop.className = 'pwa-modal-backdrop';
    backdrop.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.8);
      z-index: 10000;
      display: flex;
      align-items: center;
      justify-content: center;
    `;
    
    // Create modal
    const modal = document.createElement('div');
    modal.id = 'pwaModal';
    modal.style.cssText = `
      background: rgba(0, 0, 0, 0.95);
      border: 2px solid #0f0;
      border-radius: 5px;
      padding: 25px 30px;
      color: #0f0;
      font-family: 'Berkeley Mono', 'Courier New', monospace;
      font-size: 12px;
      text-align: center;
      box-shadow: 0 0 30px rgba(0, 255, 0, 0.6);
      max-width: 350px;
      animation: pwaModalBoot 0.3s ease-out;
    `;
    
    modal.innerHTML = `
      <div style="margin-bottom: 15px; font-weight: bold; text-transform: uppercase; font-size: 14px;">
        Install Soulforge
      </div>
      <div style="margin-bottom: 20px; opacity: 0.9; line-height: 1.4;">
        Install for fullscreen experience, offline play, and better performance
      </div>
      <div style="display: flex; flex-direction: column; gap: 10px;">
        <button id="pwaInstallBtn" style="
          background: rgba(0, 255, 0, 0.2);
          border: 1px solid #0f0;
          color: #0f0;
          padding: 10px 25px;
          cursor: pointer;
          font-family: inherit;
          text-transform: uppercase;
          font-size: 12px;
          transition: all 0.2s;
        ">Install</button>
        <button id="pwaLaterBtn" style="
          background: transparent;
          border: 1px solid #0f0;
          color: #0f0;
          padding: 8px 25px;
          cursor: pointer;
          font-family: inherit;
          text-transform: uppercase;
          font-size: 11px;
          transition: all 0.2s;
        ">Later</button>
        <button id="pwaDontAskBtn" style="
          background: transparent;
          border: 1px solid rgba(0, 255, 0, 0.5);
          color: rgba(0, 255, 0, 0.7);
          padding: 6px 25px;
          cursor: pointer;
          font-family: inherit;
          text-transform: uppercase;
          font-size: 10px;
          transition: all 0.2s;
        ">Don't Ask Again</button>
      </div>
    `;
    
    // Add animation styles
    const style = document.createElement('style');
    style.textContent = `
      @keyframes pwaModalBoot {
        0% {
          opacity: 0;
          transform: scale(0.8) translateY(20px);
          filter: blur(10px);
        }
        100% {
          opacity: 1;
          transform: scale(1) translateY(0);
          filter: blur(0);
        }
      }
      
      #pwaModal button:hover {
        background: rgba(0, 255, 0, 0.1);
        box-shadow: 0 0 10px rgba(0, 255, 0, 0.3);
      }
      
      #pwaModal button:active {
        transform: scale(0.95);
      }
      
      #pwaInstallBtn:hover {
        background: rgba(0, 255, 0, 0.3) !important;
      }
    `;
    document.head.appendChild(style);
    
    backdrop.appendChild(modal);
    document.body.appendChild(backdrop);
    
    // Event handlers
    const closeModal = () => {
      backdrop.style.opacity = '0';
      modal.style.transform = 'scale(0.9) translateY(10px)';
      setTimeout(() => backdrop.remove(), 200);
    };
    
    // Install button
    document.getElementById('pwaInstallBtn').addEventListener('click', async () => {
      closeModal();
      this.deferredPrompt.prompt();
      const { outcome } = await this.deferredPrompt.userChoice;
      console.log(`User response to install prompt: ${outcome}`);
      this.deferredPrompt = null;
    });
    
    // Later button
    document.getElementById('pwaLaterBtn').addEventListener('click', closeModal);
    
    // Don't ask again button
    document.getElementById('pwaDontAskBtn').addEventListener('click', () => {
      localStorage.setItem('soulforge_pwaPromptDismissed', 'true');
      closeModal();
    });
    
    // Click backdrop to close
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) {
        closeModal();
      }
    });
  }
}